// src/pages/SearchJobs.jsx
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FaMapMarkerAlt, 
  FaBriefcase, 
  FaClock, 
  FaStar, 
  FaFire, 
  FaBolt,
  FaFilter
} from 'react-icons/fa';
import JobCard from '../components/jobs/JobCard';
import FilterSidebar from '../components/jobs/FilterSidebar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { setDefaultSEO } from '../utils/seoUtils';
import './SearchJobs.css';
import axios from 'axios';
import { endpoints } from '../config/api';
import { extractDataFromResponse, processJobsData } from '../utils/apiUtils';
import JobStatusBadges from '../components/jobs/JobStatusBadges';
import { AdUnitJobs } from '../components/adsense';
import { scrollToTop } from '../utils/scrollUtils';

const DATE_POSTED_WINDOWS = {
  'Last Hour': { hours: 1 },
  'Last 24 hours': { hours: 24 },
  'Last 7 days': { days: 7 },
  'Last 14 days': { days: 14 },
  'Last 30 days': { days: 30 },
};

function getPostedAfterDate(datePostedFilters = []) {
  if (!datePostedFilters.length) return null;

  const now = new Date();
  let earliestCutoff = null;

  datePostedFilters.forEach((label) => {
    const window = DATE_POSTED_WINDOWS[label];
    if (!window) return;

    const cutoff = new Date(now);
    if (window.hours) {
      cutoff.setHours(cutoff.getHours() - window.hours);
    } else if (window.days) {
      cutoff.setDate(cutoff.getDate() - window.days);
    }

    if (!earliestCutoff || cutoff < earliestCutoff) {
      earliestCutoff = cutoff;
    }
  });

  return earliestCutoff ? earliestCutoff.toISOString() : null;
}

const SearchJobs = () => {

  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: [],
    jobType: [],
    location: [],
    datePosted: [],
    jobCount: {} // For showing count of jobs in each filter
  });
  const [sortBy, setSortBy] = useState('recent');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const JOBS_PER_PAGE = 100;
  const [activeTab, setActiveTab] = useState('active'); // For filtering active jobs

    const fetchJobs = async (page = 1, appliedFilters = null) => {
    console.log('Fetching jobs for page:', page);
    setLoading(true);
    try {
      // Get keyword from URL params
      const keyword = searchParams.get('keyword') || '';
      const location = searchParams.get('location') || '';
      const category = searchParams.get('category') || '';
      const jobTypeFromUrl = searchParams.get('jobType') || searchParams.get('type') || '';
      
      // Use applied filters or current filters
      const currentFilters = appliedFilters || filters;
      
      // Sync URL params into filter state
      if (category || jobTypeFromUrl) {
        setFilters(prev => ({
          ...prev,
          ...(category ? { category: [category] } : {}),
          ...(jobTypeFromUrl ? { jobType: [jobTypeFromUrl] } : {}),
        }));
      }
      
      // Build query parameters with filters
      const queryParams = {
        jobStatus: 'active',
        page: page,
        limit: JOBS_PER_PAGE,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };
      
      // Add keyword if present
      if (keyword) {
        queryParams.keyword = keyword;
      }
      
      // Add location if present
      if (location) {
        queryParams.location = location;
      }
      
      // Add category if present
      if (category) {
        queryParams.category = category;
      }

      if (jobTypeFromUrl) {
        queryParams.jobType = jobTypeFromUrl;
      }
      
      // Add filter parameters if they exist
      if (currentFilters.category.length > 0) {
        queryParams.category = currentFilters.category.join(',');
      }
      if (currentFilters.jobType.length > 0) {
        queryParams.jobType = currentFilters.jobType.join(',');
      }
      if (currentFilters.location.length > 0) {
        queryParams.location = currentFilters.location.join(',');
      }

      const postedAfter = getPostedAfterDate(currentFilters.datePosted);
      if (postedAfter) {
        queryParams.postedAfter = postedAfter;
      }
      const response = await axios.get(endpoints.jobs.list, {
        params: queryParams
      });
      
      // Extract and process jobs data using utility function
      const jobsData = extractDataFromResponse(response, 'data');
      const processedJobs = processJobsData(jobsData);

      // Extract pagination info from the response
      if (response.data?.data) {
        setTotalJobs(response.data.data.total || 0);
        setHasMore(response.data.data.hasMore || false);
      }

      // Sort jobs by posted date in descending order (newest first)
      const sortedJobs = processedJobs.sort((a, b) => {
        const dateA = new Date(a.postedDate);
        const dateB = new Date(b.postedDate);
        return dateB - dateA;
      });

      setJobs(sortedJobs);
      
      // Update job counts for filters (use all jobs, not filtered)
      updateJobCounts(sortedJobs);
      
      // Calculate total pages based on server data
      setTotalPages(Math.ceil((response.data?.data?.total || sortedJobs.length) / JOBS_PER_PAGE));
      console.log('Jobs pagination info:', {
        currentPage: page,
        totalJobs: response.data?.data?.total || 0,
        jobsOnPage: sortedJobs.length,
        totalPages: Math.ceil((response.data?.data?.total || sortedJobs.length) / JOBS_PER_PAGE),
        hasMore: response.data?.data?.hasMore || false
      });
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setError('Failed to fetch jobs. Please try again later.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(1); // Always start from page 1 when URL params change
    setCurrentPage(1);
    setDefaultSEO('Search Jobs');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Prevent body scroll when mobile filters are open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileOpen]);

  const updateJobCounts = (jobsData) => {
    const counts = {
      category: {},
      jobType: {},
      location: {}
    };

    jobsData.forEach(job => {
      // Count categories
      if (job.category) {
        counts.category[job.category] = (counts.category[job.category] || 0) + 1;
      }
      // Count job types
      if (job.jobType) {
        counts.jobType[job.jobType] = (counts.jobType[job.jobType] || 0) + 1;
      }
      // Count locations
      if (job.location) {
        counts.location[job.location] = (counts.location[job.location] || 0) + 1;
      }
    });

    setFilters(prev => ({
      ...prev,
      jobCount: counts
    }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setCurrentPage(1);
    // Apply filters immediately
    fetchJobs(1, newFilters);
    // Scroll to top to show results
    scrollToTop();
  };

  const handleKeywordSubmit = (keyword) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (keyword) p.set('keyword', keyword);
      else p.delete('keyword');
      return p;
    });
    setCurrentPage(1);
    scrollToTop();
  };

  const handleClearAllFilters = () => {
    const hadKeyword = Boolean(searchParams.get('keyword'));
    const cleared = {
      ...filters,
      category: [],
      jobType: [],
      location: [],
      datePosted: [],
    };
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('keyword');
      return p;
    });
    setFilters(cleared);
    setCurrentPage(1);
    if (!hadKeyword) {
      fetchJobs(1, cleared);
    }
    scrollToTop();
  };

  // Client-side sorting for displayed jobs
  const sortedJobs = [...jobs].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b.postedDate || b.createdAt) - new Date(a.postedDate || a.createdAt);
      case 'salary':
        return (b.salaryNumeric || 0) - (a.salaryNumeric || 0);
      default:
        return 0;
    }
  });
  
  // Add pagination handler
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Fetch jobs for the new page
    fetchJobs(pageNumber, filters);
    // Scroll to top smoothly
    scrollToTop();
  };

  if (error) {
    return (
      <div className="error-container">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => fetchJobs(currentPage, filters)}>Try Again</button>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="search-jobs-page">
      {/* SEO Meta Tags - Set via setDefaultSEO in useEffect */}
      
      <div className="search-header">
        <h1>Find Your Next Opportunity</h1>
      </div>

      <div className="search-content">
        <FilterSidebar
          filters={filters}
          onFilterChange={handleFilterChange}
          isMobileOpen={isMobileOpen}
          setIsMobileOpen={setIsMobileOpen}
          jobCount={filters.jobCount}
          urlKeyword={searchParams.get('keyword') || ''}
          onKeywordSubmit={handleKeywordSubmit}
          onClearAllFilters={handleClearAllFilters}
        />

        <div className="jobs-content">
          <div className="jobs-header">
            <span>{totalJobs} Active Jobs Found</span>
            <button 
              className="mobile-filter-btn"
              onClick={() => setIsMobileOpen(true)}
            >
              <FaFilter /> Filters
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : sortedJobs.length === 0 ? (
            <div className="no-results">
              <h3>No Active Jobs Found</h3>
              <p>Try adjusting your filters</p>
            </div>
          ) : (
            <>
              {/* Ad Before Job Listings */}
              <AdUnitJobs />

              <div className="jobs-grid">
                {sortedJobs.map((job, index) => (
                  <React.Fragment key={job._id}>
                    <JobCard job={job} />
                    {/* Add ad after every 6 jobs */}
                    {(index + 1) % 6 === 0 && <AdUnitJobs />}
                  </React.Fragment>
                ))}
              </div>

              {/* Add pagination */}
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {(currentPage - 1) * JOBS_PER_PAGE + 1} - {Math.min(currentPage * JOBS_PER_PAGE, totalJobs)} of {totalJobs} jobs
                </div>
                
                <div className="pagination">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="page-btn prev"
                  >
                    Previous
                  </button>

                  <div className="page-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                      <button
                        key={number}
                        onClick={() => handlePageChange(number)}
                        className={`page-btn number ${currentPage === number ? 'active' : ''}`}
                      >
                        {number}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="page-btn next"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchJobs;